import { useState, useEffect } from "react";
import axios from "axios";
import { 
  Server, Plus, Trash2, Edit2, RefreshCw, Terminal, 
  Key, Wifi, WifiOff, Eye, EyeOff, Loader2, CheckCircle, XCircle,
  ShieldCheck, UserCheck, UserX, Users
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Servers() {
  const { user: currentUser } = useAuth();
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [sshDialogOpen, setSshDialogOpen] = useState(false);
  const [sshServer, setSshServer] = useState(null);
  const [editingServer, setEditingServer] = useState(null);
  const [formData, setFormData] = useState({
    name: "", hostname: "", ip_address: "", os_type: "linux"
  });
  const [sshForm, setSshForm] = useState({
    username: "", password: "", private_key: "", port: 22, auth_type: "password"
  });
  const [sshHasExisting, setSshHasExisting] = useState({ password: false, key: false });
  const [sshTesting, setSshTesting] = useState(false);
  const [sshTestResult, setSshTestResult] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [collectingMetrics, setCollectingMetrics] = useState({});

  // WinRM state
  const [winrmDialogOpen, setWinrmDialogOpen] = useState(false);
  const [winrmServer, setWinrmServer] = useState(null);
  const [winrmForm, setWinrmForm] = useState({
    username: "", password: "", port: 5985, use_ssl: false, transport: "ntlm"
  });
  const [winrmHasExisting, setWinrmHasExisting] = useState(false);
  const [winrmTesting, setWinrmTesting] = useState(false);
  const [winrmTestResult, setWinrmTestResult] = useState(null);

  // Access management state
  const [accessDialogOpen, setAccessDialogOpen] = useState(false);
  const [accessServer, setAccessServer] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [grantingUser, setGrantingUser] = useState("");
  const [grantNotes, setGrantNotes] = useState("");
  const [accessLoading, setAccessLoading] = useState(false);

  const fetchServers = async () => {
    try {
      const response = await axios.get(`${API}/servers`);
      setServers(response.data);
    } catch (error) {
      toast.error("Failed to fetch servers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchServers(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingServer) {
        await axios.put(`${API}/servers/${editingServer.id}`, formData);
        toast.success("Server updated");
      } else {
        await axios.post(`${API}/servers`, formData);
        toast.success("Server added");
      }
      setIsDialogOpen(false);
      setEditingServer(null);
      setFormData({ name: "", hostname: "", ip_address: "", os_type: "linux" });
      fetchServers();
    } catch (error) {
      toast.error(editingServer ? "Failed to update server" : "Failed to add server");
    }
  };

  const handleEdit = (server) => {
    setEditingServer(server);
    setFormData({
      name: server.name, hostname: server.hostname || "",
      ip_address: server.ip_address, os_type: server.os_type
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (serverId) => {
    if (window.confirm("Delete this server and its SSH credentials?")) {
      try {
        await axios.delete(`${API}/servers/${serverId}`);
        toast.success("Server deleted");
        fetchServers();
      } catch (error) {
        toast.error("Failed to delete server");
      }
    }
  };

  const openSshDialog = async (server) => {
    setSshServer(server);
    setSshTestResult(null);
    setSshForm({ username: "root", password: "", private_key: "", port: 22, auth_type: "password" });
    setSshHasExisting({ password: false, key: false });
    
    // Check if SSH creds already exist
    try {
      const res = await axios.get(`${API}/ssh/credentials/${server.id}`);
      if (res.data && res.data.configured) {
        setSshForm(prev => ({
          ...prev,
          username: res.data.username || "root",
          port: res.data.port || 22,
          auth_type: res.data.auth_type || "password"
        }));
        setSshHasExisting({ password: res.data.has_password, key: res.data.has_private_key });
      }
    } catch (err) {
      // No creds yet, use defaults
    }
    setSshDialogOpen(true);
  };

  const saveSshCredentials = async () => {
    if (!sshServer) return;
    try {
      const payload = {
        server_id: sshServer.id,
        username: sshForm.username,
        port: sshForm.port,
        auth_type: sshForm.auth_type,
      };
      // Only include password/key if user typed a new value, or if no existing creds
      if (sshForm.auth_type === "password") {
        if (sshForm.password) payload.password = sshForm.password;
        else if (!sshHasExisting.password) { toast.error("Please enter a password"); return; }
      } else {
        if (sshForm.private_key) payload.private_key = sshForm.private_key;
        else if (!sshHasExisting.key) { toast.error("Please enter a private key"); return; }
      }
      await axios.post(`${API}/ssh/credentials`, payload);
      toast.success("SSH credentials saved");
      setSshHasExisting({
        password: sshForm.auth_type === "password" ? (!!sshForm.password || sshHasExisting.password) : false,
        key: sshForm.auth_type === "key" ? (!!sshForm.private_key || sshHasExisting.key) : false,
      });
      fetchServers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save SSH credentials");
    }
  };

  const testSshConnection = async () => {
    if (!sshServer) return;
    setSshTesting(true);
    setSshTestResult(null);
    
    // Save first (only new values), then test
    try {
      const payload = {
        server_id: sshServer.id,
        username: sshForm.username,
        port: sshForm.port,
        auth_type: sshForm.auth_type,
      };
      if (sshForm.auth_type === "password" && sshForm.password) payload.password = sshForm.password;
      if (sshForm.auth_type === "key" && sshForm.private_key) payload.private_key = sshForm.private_key;
      await axios.post(`${API}/ssh/credentials`, payload);
    } catch (err) {
      // Continue to test anyway
    }
    
    try {
      const res = await axios.post(`${API}/ssh/test?server_id=${sshServer.id}`);
      setSshTestResult({ success: true, message: res.data.message });
      toast.success("SSH connection successful!");
      fetchServers();
    } catch (error) {
      const msg = error.response?.data?.detail || "Connection failed";
      setSshTestResult({ success: false, message: msg });
      toast.error(`SSH failed: ${msg}`);
    } finally {
      setSshTesting(false);
    }
  };

  const collectMetrics = async (serverId) => {
    setCollectingMetrics(prev => ({ ...prev, [serverId]: true }));
    try {
      const res = await axios.get(`${API}/metrics/${serverId}`);
      if (res.data) {
        toast.success("Metrics collected via SSH");
        fetchServers();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to collect metrics — check SSH credentials");
    } finally {
      setCollectingMetrics(prev => ({ ...prev, [serverId]: false }));
    }
  };

  // WinRM functions
  const openWinrmDialog = async (server) => {
    setWinrmServer(server);
    setWinrmTestResult(null);
    setWinrmForm({ username: "Administrator", password: "", port: 5985, use_ssl: false, transport: "ntlm" });
    setWinrmHasExisting(false);
    try {
      const res = await axios.get(`${API}/winrm/credentials/${server.id}`);
      if (res.data && res.data.configured) {
        setWinrmForm(prev => ({
          ...prev,
          username: res.data.username || "Administrator",
          port: res.data.port || 5985,
          use_ssl: res.data.use_ssl || false,
          transport: res.data.transport || "ntlm"
        }));
        setWinrmHasExisting(res.data.has_password);
      }
    } catch {}
    setWinrmDialogOpen(true);
  };

  const saveWinrmCredentials = async () => {
    if (!winrmServer) return;
    try {
      const payload = {
        server_id: winrmServer.id,
        username: winrmForm.username,
        port: winrmForm.port,
        use_ssl: winrmForm.use_ssl,
        transport: winrmForm.transport,
      };
      if (winrmForm.password) payload.password = winrmForm.password;
      else if (!winrmHasExisting) { toast.error("Please enter a password"); return; }
      await axios.post(`${API}/winrm/credentials`, payload);
      toast.success("WinRM credentials saved");
      setWinrmHasExisting(!!winrmForm.password || winrmHasExisting);
      fetchServers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save WinRM credentials");
    }
  };

  const testWinrmConnection = async () => {
    if (!winrmServer) return;
    setWinrmTesting(true);
    setWinrmTestResult(null);
    try {
      const payload = {
        server_id: winrmServer.id,
        username: winrmForm.username,
        port: winrmForm.port,
        use_ssl: winrmForm.use_ssl,
        transport: winrmForm.transport,
      };
      if (winrmForm.password) payload.password = winrmForm.password;
      await axios.post(`${API}/winrm/credentials`, payload);
    } catch {}
    try {
      const res = await axios.post(`${API}/winrm/test?server_id=${winrmServer.id}`);
      setWinrmTestResult({ success: true, message: res.data.message });
      toast.success("WinRM connection successful!");
      fetchServers();
    } catch (error) {
      const msg = error.response?.data?.detail || "Connection failed";
      setWinrmTestResult({ success: false, message: msg });
      toast.error(`WinRM failed: ${msg}`);
    } finally {
      setWinrmTesting(false);
    }
  };

  const openAccessDialog = async (server) => {
    setAccessServer(server);
    setGrantingUser("");
    setGrantNotes("");
    setAccessLoading(true);
    setAccessDialogOpen(true);
    try {
      const [permsRes, usersRes] = await Promise.all([
        axios.get(`${API}/permissions/servers/${server.id}`),
        axios.get(`${API}/auth/users`)
      ]);
      setPermissions(permsRes.data);
      // Only show junior admins (seniors already have access)
      setAllUsers(usersRes.data.filter(u => u.role === "junior_admin"));
    } catch {
      toast.error("Failed to load access data");
    } finally {
      setAccessLoading(false);
    }
  };

  const grantAccess = async () => {
    if (!grantingUser) { toast.error("Select a user"); return; }
    try {
      const res = await axios.post(`${API}/permissions/servers`, {
        user_id: grantingUser,
        server_id: accessServer.id,
        notes: grantNotes || undefined
      });
      setPermissions(prev => [...prev, res.data]);
      setGrantingUser("");
      setGrantNotes("");
      toast.success("Direct access granted");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to grant access");
    }
  };

  const revokeAccess = async (permId) => {
    try {
      await axios.delete(`${API}/permissions/servers/${permId}`);
      setPermissions(prev => prev.filter(p => p.id !== permId));
      toast.success("Access revoked");
    } catch {
      toast.error("Failed to revoke access");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "online": return "bg-green-400";
      case "warning": return "bg-yellow-400";
      case "offline": return "bg-red-400";
      default: return "bg-zinc-400";
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "online": return "bg-green-500/10 text-green-400 border-green-500/20";
      case "warning": return "bg-yellow-500/10 text-yellow-400 border-yellow-500/20";
      case "offline": return "bg-red-500/10 text-red-400 border-red-500/20";
      default: return "bg-zinc-500/10 text-zinc-400 border-zinc-500/20";
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="servers-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-mono text-white tracking-tight">Servers</h1>
          <p className="text-zinc-400 text-sm mt-1">Manage and monitor your servers</p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={fetchServers}
            variant="outline"
            className="bg-white/5 border-white/10 hover:bg-white/10 text-white"
            data-testid="refresh-servers-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="bg-blue-600 hover:bg-blue-700 text-white"
                onClick={() => {
                  setEditingServer(null);
                  setFormData({ name: "", hostname: "", ip_address: "", os_type: "linux" });
                }}
                data-testid="add-server-btn"
              >
                <Plus className="w-4 h-4 mr-2" /> Add Server
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0c0c0e] border-white/10 text-white">
              <DialogHeader>
                <DialogTitle className="font-mono">
                  {editingServer ? "Edit Server" : "Add New Server"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Server Name</Label>
                  <Input id="name" value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Production Server 1"
                    className="bg-black/50 border-white/10 text-white" required
                    data-testid="server-name-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="hostname">Hostname <span className="text-zinc-500 text-xs font-normal">(optional)</span></Label>
                  <Input id="hostname" value={formData.hostname}
                    onChange={(e) => setFormData({ ...formData, hostname: e.target.value })}
                    placeholder="server1.example.com"
                    className="bg-black/50 border-white/10 text-white"
                    data-testid="server-hostname-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ip_address">IP Address</Label>
                  <Input id="ip_address" value={formData.ip_address}
                    onChange={(e) => setFormData({ ...formData, ip_address: e.target.value })}
                    placeholder="192.168.1.100"
                    className="bg-black/50 border-white/10 text-white" required
                    data-testid="server-ip-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="os_type">Operating System</Label>
                  <Select value={formData.os_type} onValueChange={(v) => setFormData({ ...formData, os_type: v })}>
                    <SelectTrigger className="bg-black/50 border-white/10 text-white" data-testid="server-os-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0c0c0e] border-white/10">
                      <SelectItem value="linux">Linux</SelectItem>
                      <SelectItem value="windows">Windows</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button type="button" variant="outline" className="flex-1 border-white/10 text-white"
                    onClick={() => setIsDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="save-server-btn">
                    {editingServer ? "Update" : "Add Server"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Server Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 text-blue-400 animate-spin" />
        </div>
      ) : servers.length === 0 ? (
        <Card className="bg-[#0c0c0e] border-white/5">
          <CardContent className="py-12 text-center">
            <Server className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">No servers yet</h3>
            <p className="text-zinc-400 mb-4">Add your first server to start monitoring</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {servers.map((server) => (
            <Card key={server.id} className="bg-[#0c0c0e] border-white/5 hover:border-white/10 transition-all"
              data-testid={`server-card-${server.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${getStatusColor(server.status)}`} />
                    <div>
                      <CardTitle className="text-lg font-medium text-white">{server.name}</CardTitle>
                      {server.hostname && <p className="text-sm text-zinc-500 font-mono">{server.hostname}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {server.ssh_configured && (
                      <Badge className="text-[10px] bg-green-500/10 text-green-400 border-green-500/20" data-testid={`ssh-badge-${server.id}`}>
                        <Wifi className="w-3 h-3 mr-1" /> SSH
                      </Badge>
                    )}
                    {server.winrm_configured && (
                      <Badge className="text-[10px] bg-cyan-500/10 text-cyan-400 border-cyan-500/20" data-testid={`winrm-badge-${server.id}`}>
                        <Wifi className="w-3 h-3 mr-1" /> WinRM
                      </Badge>
                    )}
                    <span className={`px-2 py-0.5 text-xs rounded-full font-mono border ${getStatusBadge(server.status)}`}>
                      {server.status}
                    </span>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-zinc-400">IP Address</span>
                  <span className="font-mono text-white">{server.ip_address}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-zinc-400">OS Type</span>
                  <span className={`px-2 py-0.5 text-xs rounded-full font-mono ${
                    server.os_type === 'linux' 
                      ? 'bg-orange-500/10 text-orange-400 border border-orange-500/20' 
                      : 'bg-blue-500/10 text-blue-400 border border-blue-500/20'
                  }`}>{server.os_type}</span>
                </div>
                
                {/* Metrics */}
                <div className="space-y-2 pt-2 border-t border-white/5">
                  {["cpu_usage", "memory_usage", "disk_usage"].map((metric) => {
                    const label = metric.replace("_usage", "").toUpperCase();
                    const val = server[metric] || 0;
                    return (
                      <div key={metric}>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-zinc-500">{label}</span>
                          <span className="text-xs font-mono text-white">{val}%</span>
                        </div>
                        <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full transition-all ${val > 80 ? 'bg-red-500' : val > 60 ? 'bg-yellow-500' : 'bg-green-500'}`}
                            style={{ width: `${val}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-2">
                  <Button variant="outline" size="sm"
                    className={`flex-1 text-xs ${server.ssh_configured 
                      ? 'bg-green-500/10 border-green-500/20 hover:bg-green-500/20 text-green-400' 
                      : 'bg-blue-500/10 border-blue-500/20 hover:bg-blue-500/20 text-blue-400'}`}
                    onClick={() => openSshDialog(server)}
                    data-testid={`ssh-config-${server.id}`}>
                    {server.ssh_configured ? <Wifi className="w-3 h-3 mr-1" /> : <Key className="w-3 h-3 mr-1" />}
                    {server.ssh_configured ? "SSH Connected" : "Setup SSH"}
                  </Button>
                  {server.ssh_configured && (
                    <Button variant="outline" size="sm"
                      className="bg-white/5 border-white/10 hover:bg-white/10 text-white text-xs"
                      onClick={() => collectMetrics(server.id)}
                      disabled={collectingMetrics[server.id]}
                      data-testid={`collect-metrics-${server.id}`}>
                      {collectingMetrics[server.id] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Terminal className="w-3 h-3" />}
                    </Button>
                  )}
                  {server.os_type === "windows" && (
                    <Button variant="outline" size="sm"
                      className={`text-xs ${server.winrm_configured
                        ? 'bg-cyan-500/10 border-cyan-500/20 hover:bg-cyan-500/20 text-cyan-400'
                        : 'bg-cyan-500/10 border-cyan-500/20 hover:bg-cyan-500/20 text-cyan-400'}`}
                      onClick={() => openWinrmDialog(server)}
                      data-testid={`winrm-config-${server.id}`}>
                      {server.winrm_configured ? <Wifi className="w-3 h-3 mr-1" /> : <Key className="w-3 h-3 mr-1" />}
                      {server.winrm_configured ? "WinRM" : "Setup WinRM"}
                    </Button>
                  )}
                  {currentUser?.role === "admin" && (
                    <Button variant="outline" size="sm"
                      className="bg-purple-500/10 border-purple-500/20 hover:bg-purple-500/20 text-purple-400"
                      onClick={() => openAccessDialog(server)}
                      title="Manage Direct Access"
                      data-testid={`manage-access-${server.id}`}>
                      <Users className="w-3 h-3" />
                    </Button>
                  )}
                  <Button variant="outline" size="sm"
                    className="bg-white/5 border-white/10 hover:bg-white/10 text-white"
                    onClick={() => handleEdit(server)}
                    data-testid={`edit-server-${server.id}`}>
                    <Edit2 className="w-3 h-3" />
                  </Button>
                  <Button variant="outline" size="sm"
                    className="bg-red-500/10 border-red-500/20 hover:bg-red-500/20 text-red-400"
                    onClick={() => handleDelete(server.id)}
                    data-testid={`delete-server-${server.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* SSH Configuration Dialog */}
      <Dialog open={sshDialogOpen} onOpenChange={setSshDialogOpen}>
        <DialogContent className="bg-[#0c0c0e] border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-mono flex items-center gap-2">
              <Terminal className="w-5 h-5 text-blue-400" />
              SSH Configuration
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              {sshServer?.name} ({sshServer?.ip_address})
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <Tabs value={sshForm.auth_type} onValueChange={(v) => setSshForm({ ...sshForm, auth_type: v })}>
              <TabsList className="bg-black/30 border border-white/5 w-full">
                <TabsTrigger value="password" className="flex-1 data-[state=active]:bg-blue-600 text-xs" data-testid="ssh-auth-password">
                  Password
                </TabsTrigger>
                <TabsTrigger value="key" className="flex-1 data-[state=active]:bg-blue-600 text-xs" data-testid="ssh-auth-key">
                  Private Key
                </TabsTrigger>
              </TabsList>
            </Tabs>

            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2 space-y-2">
                <Label className="text-zinc-400 text-xs">Username</Label>
                <Input value={sshForm.username}
                  onChange={(e) => setSshForm({ ...sshForm, username: e.target.value })}
                  placeholder="root"
                  className="bg-black/50 border-white/10 text-white font-mono"
                  data-testid="ssh-username-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400 text-xs">Port</Label>
                <Input type="number" value={sshForm.port}
                  onChange={(e) => setSshForm({ ...sshForm, port: parseInt(e.target.value) || 22 })}
                  className="bg-black/50 border-white/10 text-white font-mono"
                  data-testid="ssh-port-input" />
              </div>
            </div>

            {sshForm.auth_type === "password" ? (
              <div className="space-y-2">
                <Label className="text-zinc-400 text-xs flex items-center gap-2">
                  Password
                  {sshHasExisting.password && (
                    <span className="text-green-400 text-[10px] bg-green-500/10 border border-green-500/20 px-1.5 py-0.5 rounded">
                      saved — leave blank to keep
                    </span>
                  )}
                </Label>
                <div className="relative">
                  <Input type={showPassword ? "text" : "password"} value={sshForm.password}
                    onChange={(e) => setSshForm({ ...sshForm, password: e.target.value })}
                    placeholder={sshHasExisting.password ? "Leave blank to keep existing password" : "Enter server password"}
                    className="bg-black/50 border-white/10 text-white font-mono pr-10"
                    data-testid="ssh-password-input" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <Label className="text-zinc-400 text-xs flex items-center gap-2">
                  Private Key (PEM format)
                  {sshHasExisting.key && (
                    <span className="text-green-400 text-[10px] bg-green-500/10 border border-green-500/20 px-1.5 py-0.5 rounded">
                      saved — leave blank to keep
                    </span>
                  )}
                </Label>
                <Textarea value={sshForm.private_key}
                  onChange={(e) => setSshForm({ ...sshForm, private_key: e.target.value })}
                  placeholder={sshHasExisting.key ? "Leave blank to keep existing key" : "-----BEGIN RSA PRIVATE KEY-----\n..."}
                  className="bg-black/50 border-white/10 text-white font-mono text-xs h-32 resize-none"
                  data-testid="ssh-key-input" />
              </div>
            )}

            {/* Test Result */}
            {sshTestResult && (
              <div className={`flex items-center gap-2 p-3 rounded-lg border text-sm ${
                sshTestResult.success 
                  ? 'bg-green-500/10 border-green-500/20 text-green-400' 
                  : 'bg-red-500/10 border-red-500/20 text-red-400'
              }`} data-testid="ssh-test-result">
                {sshTestResult.success ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                {sshTestResult.message}
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1 border-white/10 text-white"
                onClick={saveSshCredentials} data-testid="save-ssh-btn">
                Save Credentials
              </Button>
              <Button className="flex-1 bg-blue-600 hover:bg-blue-700"
                onClick={testSshConnection} disabled={sshTesting}
                data-testid="test-ssh-btn">
                {sshTesting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Testing...</>
                ) : (
                  <><Wifi className="w-4 h-4 mr-2" /> Test Connection</>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* WinRM Configuration Dialog */}
      <Dialog open={winrmDialogOpen} onOpenChange={setWinrmDialogOpen}>
        <DialogContent className="bg-[#0c0c0e] border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-mono flex items-center gap-2">
              <Terminal className="w-5 h-5 text-cyan-400" />
              WinRM Configuration
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              {winrmServer?.name} ({winrmServer?.ip_address})
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-zinc-400 text-xs">Username</Label>
                <Input value={winrmForm.username}
                  onChange={(e) => setWinrmForm({ ...winrmForm, username: e.target.value })}
                  placeholder="Administrator"
                  className="bg-black/50 border-white/10 text-white font-mono"
                  data-testid="winrm-username-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400 text-xs">Port</Label>
                <Input type="number" value={winrmForm.port}
                  onChange={(e) => setWinrmForm({ ...winrmForm, port: parseInt(e.target.value) || 5985 })}
                  className="bg-black/50 border-white/10 text-white font-mono"
                  data-testid="winrm-port-input" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-zinc-400 text-xs">Transport</Label>
                <Select value={winrmForm.transport} onValueChange={v => setWinrmForm({ ...winrmForm, transport: v })}>
                  <SelectTrigger className="bg-black/50 border-white/10 text-white text-xs" data-testid="winrm-transport-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0c0c0e] border-white/10">
                    <SelectItem value="ntlm">NTLM</SelectItem>
                    <SelectItem value="basic">Basic</SelectItem>
                    <SelectItem value="kerberos">Kerberos</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400 text-xs">SSL</Label>
                <button onClick={() => setWinrmForm({ ...winrmForm, use_ssl: !winrmForm.use_ssl })}
                  className={`w-full h-9 rounded-md border text-xs font-medium transition-all ${
                    winrmForm.use_ssl ? "bg-cyan-500/20 border-cyan-500/30 text-cyan-400" : "bg-black/50 border-white/10 text-zinc-400"
                  }`} data-testid="winrm-ssl-toggle">
                  {winrmForm.use_ssl ? "HTTPS (5986)" : "HTTP (5985)"}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-zinc-400 text-xs flex items-center gap-2">
                Password
                {winrmHasExisting && (
                  <span className="text-green-400 text-[10px] bg-green-500/10 border border-green-500/20 px-1.5 py-0.5 rounded">
                    saved — leave blank to keep
                  </span>
                )}
              </Label>
              <div className="relative">
                <Input type={showPassword ? "text" : "password"} value={winrmForm.password}
                  onChange={(e) => setWinrmForm({ ...winrmForm, password: e.target.value })}
                  placeholder={winrmHasExisting ? "Leave blank to keep existing" : "Enter Windows password"}
                  className="bg-black/50 border-white/10 text-white font-mono pr-10"
                  data-testid="winrm-password-input" />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {winrmTestResult && (
              <div className={`flex items-center gap-2 p-3 rounded-lg border text-sm ${
                winrmTestResult.success
                  ? 'bg-green-500/10 border-green-500/20 text-green-400'
                  : 'bg-red-500/10 border-red-500/20 text-red-400'
              }`} data-testid="winrm-test-result">
                {winrmTestResult.success ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                {winrmTestResult.message}
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1 border-white/10 text-white"
                onClick={saveWinrmCredentials} data-testid="save-winrm-btn">
                Save Credentials
              </Button>
              <Button className="flex-1 bg-cyan-600 hover:bg-cyan-700"
                onClick={testWinrmConnection} disabled={winrmTesting}
                data-testid="test-winrm-btn">
                {winrmTesting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Testing...</>
                ) : (
                  <><Wifi className="w-4 h-4 mr-2" /> Test WinRM</>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Access Management Dialog */}
      <Dialog open={accessDialogOpen} onOpenChange={setAccessDialogOpen}>
        <DialogContent className="bg-[#0c0c0e] border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-mono flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-purple-400" />
              Manage Direct Access
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              {accessServer?.name} — Grant users permission to execute commands directly without approval
            </DialogDescription>
          </DialogHeader>

          {accessLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-purple-400" />
            </div>
          ) : (
            <div className="space-y-5">
              {/* Grant new access */}
              <div className="space-y-3 p-3 bg-purple-500/5 border border-purple-500/10 rounded-lg">
                <p className="text-xs text-zinc-400 font-medium uppercase tracking-wider">Grant Direct Access</p>
                <div className="flex gap-2">
                  <Select value={grantingUser} onValueChange={setGrantingUser}>
                    <SelectTrigger className="flex-1 bg-black/50 border-white/10 text-white text-xs h-8" data-testid="grant-user-select">
                      <SelectValue placeholder="Select junior admin..." />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0c0c0e] border-white/10">
                      {allUsers.length === 0 ? (
                        <SelectItem value="none" disabled>No junior admins found</SelectItem>
                      ) : (
                        allUsers
                          .filter(u => !permissions.find(p => p.user_id === u.id))
                          .map(u => (
                            <SelectItem key={u.id} value={u.id}>
                              <span className="text-xs">{u.name || u.email}</span>
                            </SelectItem>
                          ))
                      )}
                    </SelectContent>
                  </Select>
                  <Button size="sm" className="bg-purple-600 hover:bg-purple-700 text-white h-8 text-xs"
                    onClick={grantAccess} disabled={!grantingUser}
                    data-testid="grant-access-btn">
                    <UserCheck className="w-3.5 h-3.5 mr-1" /> Grant
                  </Button>
                </div>
                <Input
                  value={grantNotes}
                  onChange={(e) => setGrantNotes(e.target.value)}
                  placeholder="Optional note (reason for access)"
                  className="bg-black/50 border-white/10 text-white text-xs h-8"
                  data-testid="grant-notes-input"
                />
              </div>

              {/* Existing permissions */}
              <div className="space-y-2">
                <p className="text-xs text-zinc-400 font-medium uppercase tracking-wider">
                  Users with Direct Access ({permissions.length})
                </p>
                {permissions.length === 0 ? (
                  <p className="text-xs text-zinc-600 py-3 text-center">No direct access granted yet</p>
                ) : (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {permissions.map(perm => {
                      const userInfo = allUsers.find(u => u.id === perm.user_id);
                      return (
                        <div key={perm.id}
                          className="flex items-center justify-between p-2.5 bg-white/5 border border-white/5 rounded-lg"
                          data-testid={`permission-row-${perm.id}`}>
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-purple-500/20 flex items-center justify-center">
                              <UserCheck className="w-3.5 h-3.5 text-purple-400" />
                            </div>
                            <div>
                              <p className="text-sm text-white">{userInfo?.name || userInfo?.email || perm.user_id}</p>
                              <p className="text-[10px] text-zinc-500">
                                Granted by {perm.granted_by_email} · {new Date(perm.granted_at).toLocaleDateString()}
                              </p>
                              {perm.notes && <p className="text-[10px] text-zinc-400 italic">{perm.notes}</p>}
                            </div>
                          </div>
                          <Button variant="outline" size="sm"
                            className="bg-red-500/10 border-red-500/20 hover:bg-red-500/20 text-red-400 h-7 text-xs"
                            onClick={() => revokeAccess(perm.id)}
                            data-testid={`revoke-access-${perm.id}`}>
                            <UserX className="w-3 h-3 mr-1" /> Revoke
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="p-2.5 bg-blue-500/5 border border-blue-500/10 rounded-lg">
                <p className="text-[11px] text-blue-300">
                  <span className="font-medium">Note:</span> Senior Admins and Admins always have direct access. 
                  Direct access users skip the approval queue — commands execute immediately on the server.
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
